import { useMemo, useState } from 'react';
import { layoutChain, closureSummary } from '../../utils/chainLayout';
import { Ruler, Maximize2, EyeOff, Link2 } from 'lucide-react';
import { useToleranceStore } from '../../stores/toleranceStore';
import { loadChainViewDefault, saveChainViewDefault } from '../../utils/chainViewDefaults';
import { fmt } from './measure/numFormat';

// MassketteDiagram (Phase 4) — replaces the slim ChainDisplay carried
// through Phase 3. Per the handoff prototype (masskette.jsx) the
// diagram is now a fragment of two siblings:
//
//   1. A section heading ("TOLERANZKETTE  M₀ = 50 ± 1,300 mm · 3 Maße")
//      with the Geometrie/Toleranz scale toggle on the right. No card
//      chrome — sits directly in the parent flex column so it mirrors
//      the MeasureTable heading style.
//
//   2. A bordered diagram card containing the SVG + a legend strip.
//
// Layout is responsive via viewBox + preserveAspectRatio so the SVG
// scales with the surrounding card. Compact mode (default) packs the
// chain tighter for narrower viewports.
//
// View modes:
//   - toleranz  → arrow length follows the element's tolerance band
//                 (visual emphasis on which element eats budget) — the
//                 default per the latest hand-off, since the chain
//                 picture is more diagnostic in tolerance mode.
//   - nennmass  → arrow length follows the element's nominal value
//                 (the actual chain layout); was called "geometrie"
//                 pre-rename, renamed to match the rest of the UI's
//                 "Nennmaß" vocabulary.
//
// Initial view comes from chainViewDefaults (localStorage-backed) so
// users who prefer Nennmaß can flip the default once in Settings →
// Anzeige. Toggling the in-app switch also persists the choice so the
// preference is sticky without having to re-open Settings.

// controls (C7): zusätzliche Bedienelemente rechts in der Kopfzeile.
export default function MassketteDiagram({ onHide, controls = null }) {
  const elements = useToleranceStore((s) => s.elements);
  const [view, setViewState] = useState(loadChainViewDefault);
  const setView = (next) => { setViewState(next); saveChainViewDefault(next); };
  const compact = true;

  // ── Geometry constants (mirrors prototype compact mode) ────────
  const W = 920;
  const padX = compact ? 44 : 60;
  const rowH = compact ? 34 : 80;
  const chipW = compact ? 38 : 86;
  const chipH = compact ? 20 : 38;
  const yBase = compact ? 16 : 32;

  const { items, m0Data, minX, span, levelCount } = useMemo(() => layoutChain(elements, view), [elements, view]);
  // Verbinder und Endpunkte nur zwischen aktiven Maßen (C5).
  const chainItems = items.filter((it) => !it.inactive);

  // Closure summary for the section-heading tagline (nur aktive Maße, C5)
  const { nominal: closureNominal, tolU: closureTolU, tolL: closureTolL, sym: closureSym } = closureSummary(elements);

  // Geometry helpers (only valid when items exist; guarded below).
  const innerW = W - padX * 2;
  const scale = items.length ? innerW / span : 1;
  const xFor = (v) => padX + (v - minX) * scale;
  const yFor = (lvl) => yBase + lvl * rowH;
  const H = levelCount * rowH + (compact ? 8 : 24);

  const C = {
    positive: 'var(--color-ok)',
    negative: 'var(--color-err)',
    brand: 'var(--color-brand)',
    line: 'var(--color-line)',
    lineStrong: 'var(--color-line-strong)',
    ink2: 'var(--color-ink-2)',
    ink3: 'var(--color-ink-3)',
    ink4: 'var(--color-ink-4)',
  };

  // ── Section heading (matches MeasureTable header pattern) ──────

  const heading = (
    <div style={{ display: 'flex', alignItems: 'baseline', gap: 12, paddingLeft: 4, flexWrap: 'wrap', rowGap: 6 }}>
      <h2 className="text-ink-2" style={{ fontSize: 13, fontWeight: 600, margin: 0, letterSpacing: 0.2, textTransform: 'uppercase' }}>
        Toleranzkette
      </h2>
      {elements.length > 0 && chainItems.length === 0 && (
        <span className="num text-ink-3" style={{ fontSize: 11 }}>
          alle Maße inaktiv · {elements.length} {elements.length === 1 ? 'Maß' : 'Maße'}
        </span>
      )}
      {chainItems.length > 0 && (
        <span className="num text-ink-3" style={{ fontSize: 11 }}>
          M₀ = {fmt(closureNominal, 2)}
          {closureSym ? (
            <> ± {fmt(Math.abs(closureTolU), 3)}</>
          ) : (
            <> +{fmt(closureTolU, 3)} / {closureTolL >= 0 ? '+' : '−'}{fmt(Math.abs(closureTolL), 3)}</>
          )} mm · {elements.length} {elements.length === 1 ? 'Maß' : 'Maße'}
        </span>
      )}
      <span style={{ flex: 1 }} />
      <div
        role="tablist"
        aria-label="Skalierung"
        className="bg-surface border border-line inline-flex"
        style={{ borderRadius: 'var(--radius-md)', padding: 2, alignSelf: 'center' }}
      >
        {[
          { id: 'toleranz', label: 'Toleranz', icon: Maximize2 },
          { id: 'nennmass', label: 'Nennmaß',  icon: Ruler },
        ].map((t) => {
          const Icon = t.icon;
          const active = view === t.id;
          return (
            <button
              key={t.id}
              type="button"
              role="tab"
              aria-selected={active}
              onClick={() => setView(t.id)}
              className={active ? 'bg-page text-brand' : 'text-ink-3'}
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 6,
                height: 24, padding: '0 10px', border: 'none',
                background: active ? 'var(--color-page)' : 'transparent',
                fontSize: 11, fontWeight: active ? 600 : 500,
                borderRadius: 6, cursor: 'pointer',
              }}
            >
              <Icon size={11} aria-hidden="true" />
              {t.label}
            </button>
          );
        })}
      </div>
      {controls}
      {onHide && (
        <button
          type="button"
          onClick={onHide}
          title="Diagramm ausblenden"
          className="bg-surface border border-line text-ink-3 hover:text-ink-2"
          style={{
            display: 'inline-flex', alignItems: 'center', gap: 5,
            height: 28, padding: '0 10px', alignSelf: 'center',
            borderRadius: 'var(--radius-md)', cursor: 'pointer',
            fontSize: 11, fontWeight: 600,
          }}
        >
          <EyeOff size={12} aria-hidden="true" /> Ausblenden
        </button>
      )}
    </div>
  );

  // ── Diagram card ───────────────────────────────────────────────

  const card = (
    <div
      data-snippet="masskette"
      data-tour="chain-diagram"
      className="bg-surface border border-line"
      style={{ borderRadius: 'var(--radius-lg)', overflow: 'hidden', flexShrink: 0 }}
    >
      <div
        className="bg-surface-2"
        style={{ padding: compact ? '6px 14px 8px' : '14px 16px 14px', minHeight: compact ? 'auto' : 150 }}
      >
        {/* Legend ribbon */}
        {elements.length > 0 && (
          <div
            className="text-ink-3"
            style={{ display: 'flex', gap: 14, fontSize: 10.5, alignItems: 'center', marginBottom: compact ? 2 : 6, paddingLeft: 2 }}
          >
            <LegendSwatch color={C.positive} label="+ Wirkrichtung" />
            <LegendSwatch color={C.negative} label="− Wirkrichtung" />
            <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <span style={{ width: 18, height: 0, borderTop: '1.5px dashed var(--color-brand)', display: 'inline-block' }} />
              <span className="text-ink-2">Schließmaß M₀</span>
            </span>
          </div>
        )}

        {!elements.length ? (
          // Empty state — minimal: chain icon + label. The Onboarding-
          // CTA lives in the MeasureTable's EmptyChainHint, no need to
          // duplicate it here.
          <div
            className="text-ink-4"
            style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 8, height: 160 }}
          >
            <Link2 size={28} aria-hidden="true" style={{ opacity: 0.45 }} />
            <span className="text-ink-3" style={{ fontSize: 12, fontWeight: 500 }}>
              Noch keine Toleranzkette
            </span>
          </div>
        ) : (
          <svg width="100%" viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="xMidYMid meet">
            {/* Vertical dimension hairlines connecting endpoint nodes */}
            {chainItems.length > 0 && (
              <line
                x1={xFor(0)} x2={xFor(0)}
                y1={yFor(m0Data.level)} y2={yFor(chainItems[0].level)}
                stroke={C.lineStrong} strokeWidth="1.2" strokeDasharray="3 3"
              />
            )}
            {chainItems.map((it, i) => {
              const next = chainItems[i + 1];
              const x = xFor(it.end);
              const y1 = yFor(it.level);
              const y2 = next ? yFor(next.level) : yFor(m0Data.level);
              return (
                <line key={`c${it.id}`} x1={x} x2={x} y1={y1} y2={y2}
                      stroke={C.lineStrong} strokeWidth="1.2" strokeDasharray="3 3" />
              );
            })}

            {/* M₀ closure arrow first — chip rendered LAST so it sits
                on top of any vertical connector that lands inside its
                bounding box (the user flagged the M₀ box being cut by
                an endpoint-connector vertical in the default-values
                example). */}
            {chainItems.length > 0 && <Arrow x1={xFor(0)} x2={xFor(m0Data.end)} y={yFor(m0Data.level)} color={C.brand} dashed compact={compact} />}

            {/* Endpoint node markers */}
            {chainItems.length > 0 && <circle cx={xFor(0)} cy={yFor(m0Data.level)} r={2.5} fill={C.ink3} />}
            {chainItems.map((it) => (
              <circle key={`n${it.id}`} cx={xFor(it.end)} cy={yFor(it.level)} r={2.5} fill={C.ink3} />
            ))}

            {/* Chain arrows — labels are LABEL-ONLY per user feedback
                ("Maßkennwerte raus, Tooltip rein"). The full nominal +
                tolerance + direction info lives in the SVG `<title>`
                tooltip so it's a hover away without crowding the chip.
                Arrows get a 4 px inset at both ends so neighbouring
                segments don't kiss. */}
            {items.map((it) => {
              const x1raw = xFor(it.start);
              const x2raw = xFor(it.end);
              const dir = x2raw >= x1raw ? 1 : -1;
              const x1 = x1raw + 4 * dir;
              const x2 = x2raw - 4 * dir;
              const y = yFor(it.level);
              const color = it.inactive ? C.ink4 : it.isPositive ? C.positive : C.negative;
              const tolStr = Math.abs(it.upper + it.lower) < 1e-6
                ? `±${fmt(Math.abs(it.upper), 3)}`
                : `${it.upper >= 0 ? '+' : '−'}${fmt(Math.abs(it.upper), 3)} / ${it.lower >= 0 ? '+' : '−'}${fmt(Math.abs(it.lower), 3)}`;
              const tip = `${it.label}${it.name ? ' · ' + it.name : ''} · ${it.isPositive ? '+' : '−'}${fmt(Math.abs(it.nominal), 3)} mm ${tolStr}${it.inactive ? ' · inaktiv, nicht berechnet' : ''}`;
              return (
                <g key={it.id}>
                  <title>{tip}</title>
                  <Arrow x1={x1} x2={x2} y={y} color={color} dashed={it.inactive} compact={compact} />
                  <Chip
                    x={(x1 + x2) / 2}
                    y={y}
                    label={it.label}
                    ribbon={color}
                    w={chipW}
                    h={chipH}
                  />
                </g>
              );
            })}

            {/* M₀ chip drawn LAST so the closure label sits on top of
                everything including the vertical endpoint connectors
                that may pass through its bounding box. */}
            {chainItems.length > 0 && (<g>
              <title>
                Schließmaß M₀ = {fmt(closureNominal, 2)} mm{' '}
                {closureSym
                  ? `± ${fmt(Math.abs(closureTolU), 3)}`
                  : `+${fmt(Math.abs(closureTolU), 3)} / ${closureTolL >= 0 ? '+' : '−'}${fmt(Math.abs(closureTolL), 3)}`}
              </title>
              <Chip
                x={(xFor(0) + xFor(m0Data.end)) / 2}
                y={yFor(m0Data.level)}
                label="M₀"
                ribbon={C.brand}
                dim
                w={chipW}
                h={chipH}
              />
            </g>)}
          </svg>
        )}
      </div>
    </div>
  );

  return (<>
    {heading}
    {card}
  </>);
}

// ─── Small atoms (module scope so react-hooks/static-components is happy) ─

function Arrow({ x1, x2, y, color, dashed, compact = true }) {
  const dir = x2 >= x1 ? 1 : -1;
  const head = compact ? 6 : 8;
  const sw = compact ? 1.1 : 1.4;
  const tipBase = x2 - dir * head;
  return (
    <g fill="none" stroke={color} strokeWidth={sw} strokeLinecap="round" strokeLinejoin="round"
       strokeDasharray={dashed ? '5 4' : undefined}>
      <line x1={x1} x2={x2} y1={y} y2={y} />
      <circle cx={x1} cy={y} r={sw + 0.6} fill={color} stroke="none" />
      <path d={`M ${tipBase} ${y - head * 0.55} L ${x2} ${y} L ${tipBase} ${y + head * 0.55}`} />
    </g>
  );
}

function Chip({ x, y, label, ribbon, dim, w, h }) {
  return (
    <g transform={`translate(${x - w / 2},${y - h / 2})`}>
      <rect width={w} height={h} rx="4"
            fill="var(--color-surface)"
            stroke={dim ? 'var(--color-brand)' : 'var(--color-line)'}
            strokeWidth="1" />
      <rect x="0" y="0" width="3" height={h} rx="1.5" fill={ribbon} />
      <text x={w / 2 + 1} y={h / 2 + 4} fontSize="10" textAnchor="middle"
            fill="var(--color-ink)" style={{ fontFamily: 'var(--font-mono)', fontWeight: 600 }}>
        {label}
      </text>
    </g>
  );
}

function LegendSwatch({ color, label }) {
  return (
    <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
      <span
        className="bg-surface border border-line"
        style={{ width: 12, height: 12, borderRadius: 2, position: 'relative', display: 'inline-block' }}
      >
        <span
          style={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: 3, borderRadius: '1px 0 0 1px', background: color }}
        />
      </span>
      <span className="text-ink-2">{label}</span>
    </span>
  );
}
